import React, { useState, useRef } from "react";
import { UploadCloud, X, Trash2, Maximize2, Image as ImageIcon } from "lucide-react";
import toast from "react-hot-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient, getValidToken } from "../../apiClient";

// --- Cloudinary Helper ---
const uploadToCloudinary = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", "juxso4ls"); // Your existing preset

  const res = await fetch("https://api.cloudinary.com/v1_1/mayur28/image/upload", {
    method: "POST",
    body: formData,
  });
  if (!res.ok) throw new Error("Cloudinary upload failed");
  return res.json();
};

const Images = () => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);

  // State
  const [stagedFiles, setStagedFiles] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [activeGroup, setActiveGroup] = useState("All");
  const [previewImage, setPreviewImage] = useState(null);

  // --- API Hooks ---
  const { data: { images = [], groups = ["All"] } = {}, isLoading } = useQuery({
    queryKey: ["images"],
    queryFn: async () => {
      const { userid } = await getValidToken();
      const data = await apiClient(`/problems/getnotes/${userid}`);
      
      let parsedImages = [];
      let parsedGroups = ["All"];

      data.totalnote.forEach((item) => {
        if (item.title && item.title.startsWith("image_")) {
          if (item.group && !parsedGroups.includes(item.group)) {
            parsedGroups.push(item.group);
          }
          // Clean up the wrapped <p> tags from the old editor format
          let cleanNote = item.note;
          if (cleanNote.startsWith("<p>")) {
            cleanNote = cleanNote.substring(3, cleanNote.length - 4);
          }
          parsedImages.push({
            ...item,
            displayTitle: item.title.substring(6),
            url: cleanNote,
          });
        }
      });
      return { images: parsedImages, groups: parsedGroups };
    },
  });

  const { mutate: deleteImage } = useMutation({
    mutationFn: (id) => apiClient("/problems/deletenote", {
      method: "DELETE",
      body: JSON.stringify({ _id: id }), // apiClient auto-injects userid
    }),
    onSuccess: () => {
      toast.success("Image deleted");
      queryClient.invalidateQueries(["images"]);
    },
    onError: (err) => toast.error(err.message),
  });

  const [isUploading, setIsUploading] = useState(false);

  // --- Handlers ---
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Validation
    const validFiles = files.filter(file => {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 5MB`);
        return false;
      }
      return true;
    });

    setStagedFiles(prev => [...prev, ...validFiles].slice(0, 8)); // Max 8 limit like before
  };

  const removeStagedFile = (index) => {
    setStagedFiles(stagedFiles.filter((_, i) => i !== index));
  };

  const handleUploadSubmit = async () => {
    if (stagedFiles.length === 0) return;
    setIsUploading(true);
    
    try {
      await getValidToken();
      
      // 1. Upload all to Cloudinary sequentially (or Promise.all)
      const uploadedNotes = [];
      for (const file of stagedFiles) {
        const cloudinaryRes = await uploadToCloudinary(file);
        uploadedNotes.push({
          title: "image_" + cloudinaryRes.original_filename,
          note: cloudinaryRes.secure_url,
          group: groupName.trim(),
        });
      }

      // 2. Save to database
      await apiClient("/problems/addnote", {
        method: "POST",
        body: JSON.stringify({ notes: uploadedNotes }),
      });

      toast.success("Images uploaded successfully!");
      setStagedFiles([]);
      setGroupName("");
      queryClient.invalidateQueries(["images"]);
    } catch (err) {
      toast.error(err.message || "Failed to upload images");
    } finally {
      setIsUploading(false);
    }
  };

  // Filtered Data
  const filteredImages = activeGroup === "All" 
    ? images 
    : images.filter(img => img.group === activeGroup);

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      
      {/* Upload Section */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
        <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
          <UploadCloud className="w-6 h-6 text-blue-600" />
          Upload Images
        </h2>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Dropzone / Select Area */}
          <div className="flex-1">
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-slate-300 rounded-xl p-8 flex flex-col items-center justify-center text-center cursor-pointer hover:bg-slate-50 hover:border-blue-400 transition-colors h-full min-h-[200px]"
            >
              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleFileSelect} 
                multiple 
                accept="image/*" 
                className="hidden" 
              />
              <div className="bg-blue-50 p-3 rounded-full mb-4">
                <ImageIcon className="w-8 h-8 text-blue-600" />
              </div>
              <p className="font-medium text-slate-700">Click to select images</p>
              <p className="text-sm text-slate-400 mt-1">PNG, JPG, GIF up to 5MB (Max 8 files)</p>
            </div>
          </div>

          {/* Staging & Action Area */}
          <div className="w-full lg:w-80 flex flex-col justify-between space-y-6">
            
            {/* Selected Files Preview */}
            <div className="flex-1 bg-slate-50 rounded-xl border border-slate-100 p-4 overflow-y-auto max-h-[200px]">
              <h3 className="text-sm font-semibold text-slate-600 mb-3">
                Selected Files ({stagedFiles.length})
              </h3>
              {stagedFiles.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No files selected</p>
              ) : (
                <div className="space-y-2">
                  {stagedFiles.map((file, i) => (
                    <div key={i} className="flex items-center justify-between bg-white px-3 py-2 rounded-lg border border-slate-200 shadow-sm">
                      <span className="text-sm text-slate-700 truncate pr-2">{file.name}</span>
                      <button onClick={() => removeStagedFile(i)} className="text-slate-400 hover:text-red-500 transition-colors">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Inputs & Submit */}
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Group / Folder Name</label>
                <input
                  type="text"
                  placeholder="e.g. Graphs, DP..."
                  value={groupName}
                  maxLength={15}
                  onChange={(e) => setGroupName(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <button
                onClick={handleUploadSubmit}
                disabled={stagedFiles.length === 0 || isUploading}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50"
              >
                {isUploading ? "Uploading..." : "Start Upload"}
                {!isUploading && <UploadCloud className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Gallery Section */}
      <div className="space-y-6">
        
        {/* Tabs */}
        {groups.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {groups.map((group) => (
              <button
                key={group}
                onClick={() => setActiveGroup(group)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                  activeGroup === group 
                    ? "bg-slate-800 text-white shadow-md" 
                    : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
                }`}
              >
                {group || "Ungrouped"}
              </button>
            ))}
          </div>
        )}

        {/* Grid */}
        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 animate-pulse">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="aspect-square bg-slate-200 rounded-xl" />
            ))}
          </div>
        ) : filteredImages.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-slate-200 border-dashed">
            <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No images found in this group.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {filteredImages.map((img) => (
              <div 
                key={img._id} 
                className="group relative aspect-square rounded-xl overflow-hidden bg-slate-100 border border-slate-200 shadow-sm"
              >
                <img 
                  src={img.url} 
                  alt={img.displayTitle} 
                  className="w-full h-full object-cover"
                />
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col justify-between p-3">
                  <div className="flex justify-end gap-2">
                    <button 
                      onClick={() => deleteImage(img._id)}
                      className="p-1.5 bg-white/20 hover:bg-red-500 text-white rounded-lg backdrop-blur-sm transition-colors"
                      title="Delete Image"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <p className="text-white text-xs font-medium truncate pr-2">
                      {img.displayTitle}
                    </p>
                    <button 
                      onClick={() => setPreviewImage(img)}
                      className="p-1.5 bg-white/20 hover:bg-white/40 text-white rounded-lg backdrop-blur-sm transition-colors shrink-0"
                      title="Expand"
                    >
                      <Maximize2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {previewImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-sm" onClick={() => setPreviewImage(null)} />
          <div className="relative max-w-5xl w-full flex flex-col items-center">
            <button 
              onClick={() => setPreviewImage(null)}
              className="absolute -top-12 right-0 p-2 text-white hover:text-slate-300 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            <img 
              src={previewImage.url} 
              alt={previewImage.displayTitle} 
              className="max-h-[85vh] rounded-lg shadow-2xl object-contain"
            />
            <p className="text-white font-medium mt-4 bg-black/50 px-4 py-2 rounded-full">
              {previewImage.displayTitle}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Images;